import { CommonModule } from '@angular/common';
import _moment from 'moment';

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';

import { Pessoa } from '../../../models/Pessoa';
import { EmpresasService } from '../../../services/empresa/empresas.service';
import { PessoasService } from '../../../services/pessoa/pessoas.service';
import { getPaginatorIntl } from '../../../shared/mat-paginator-intl';
import { AuthService } from '../../../services/auth/auth.service';
import { Router } from '@angular/router';
import { ExportarDadosRequest } from '../../../dto/exportacao/ExportarDadosDTO';
import { ExportarDadosService } from '../../../services/exportar-dados/exportar-dados.service';

export const MY_DATE_FORMATS = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

interface PessoaTable extends Pessoa {
  selecionada?: boolean;
}

@Component({
  selector: 'app-exportar-dados',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
  ],
  templateUrl: './exportar-dados.html',
  styleUrl: './exportar-dados.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    provideMomentDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MatPaginatorIntl, useFactory: getPaginatorIntl },
  ],
})
export class ExportarDados implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // ── Empresas ──────────────────────────────────────────────
  listaDeEmpresasOriginal: any[] = [];
  listaDeEmpresas: any[] = [];
  empresaSelecionada: any = null;

  // ── Endereços ─────────────────────────────────────────────
  listaDeEnderecosOriginal: any[] = [];
  listaDeEnderecos: any[] = [];
  enderecoSelecionado: any = null;

  // ── Pessoas ───────────────────────────────────────────────
  isLoadingPessoas = false;
  pessoas = new MatTableDataSource<PessoaTable>();
  listaCompleta: PessoaTable[] = [];
  pessoasSelecionadasLocal: Pessoa[] = [];
  todosSelecionados = false;

  colunasTabela = [
    { id: 'nomeCompleto', header: 'Nome', propriedade: 'nomeCompleto' },
    { id: 'numeroDocumento', header: 'Documento', propriedade: 'numeroDocumento' },
    { id: 'cargo', header: 'Cargo', propriedade: 'cargo' },
    { id: 'telefoneCelular', header: 'Telefone', propriedade: 'telefoneCelular' },
    { id: 'empresaNomeFantasia', header: 'Empresa', propriedade: 'empresaNomeFantasia' },
  ];

  get displayedColumns(): string[] {
    return ['selecionar', ...this.colunasTabela.map((c) => c.id)];
  }

  pageSize = 10;
  pageSizeOptions = [5, 10, 15];
  total = 0;
  inicio = 1;
  fim = 10;

  // ── Período ───────────────────────────────────────────────
  minData = new Date(1900, 0, 1);
  maxData = new Date();
  dataInicio: Date | null = null;
  dataFim: Date | null = null;

  get dataFimMin(): Date {
    return this.dataInicio ?? this.minData;
  }

  get dataInicioMax(): Date {
    return this.dataFim ?? this.maxData;
  }

  get algunsPessoasSelecionados(): boolean {
    return (
      this.pessoasSelecionadasLocal.length > 0 &&
      this.pessoasSelecionadasLocal.length < this.listaCompleta.length
    );
  }

  get formularioValido(): boolean {
    return (
      !!this.empresaSelecionada &&
      !!this.enderecoSelecionado &&
      this.pessoasSelecionadasLocal.length > 0 &&
      !!this.dataInicio &&
      !!this.dataFim
    );
  }

  constructor(
    private empresasService: EmpresasService,
    private pessoasService: PessoasService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private router: Router,
    private exportarDadosService: ExportarDadosService
  ) { }

  async ngOnInit(): Promise<void> {
    if (!this.authService.isSaude() && !this.authService.isMaster()) {
      this.router.navigate(['/home']);
      return;
    }

    await this.carregarEmpresas();
  }

  ngAfterViewInit(): void {
    this.pessoas.paginator = this.paginator;
    this.pessoas.sort = this.sort;

    this.pessoas.filterPredicate = (data: PessoaTable, filter: string) => {
      const termo = filter.trim().toLowerCase();
      return (
        (data.nomeCompleto || '').toLowerCase().includes(termo) ||
        (data.numeroDocumento || '').toLowerCase().includes(termo)
      );
    };
  }

  // ── Empresas ──────────────────────────────────────────────

  async carregarEmpresas(): Promise<void> {
    this.listaDeEmpresasOriginal = await this.empresasService.consultaTelaVincularEmpresaAPessoa();
    this.listaDeEmpresas = [...this.listaDeEmpresasOriginal];
    this.cdr.detectChanges();
  }

  filtrarEmpresas(event: Event): void {
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.listaDeEmpresas = query
      ? this.listaDeEmpresasOriginal.filter((e) =>
        (e.nomeFantasia || '').toLowerCase().includes(query)
      )
      : [...this.listaDeEmpresasOriginal];
  }

  toggleEmpresa(empresa: any, checked: boolean): void {
    if (this.empresaSelecionada) {
      this.empresaSelecionada.selecionado = false;
      this.listaDeEnderecosOriginal.forEach(e => e.selecionado = false);

    }

    empresa.selecionado = checked;
    this.empresaSelecionada = checked ? empresa : null;

    this.enderecoSelecionado = null;
    this.listaDeEnderecosOriginal = [];
    this.listaDeEnderecos = [];
    this.limparPessoas();

    if (checked) {
      this.listaDeEnderecosOriginal = empresa.enderecos || [];
      this.listaDeEnderecos = [...this.listaDeEnderecosOriginal];
    }

    this.cdr.detectChanges();
  }

  // ── Endereços ─────────────────────────────────────────────

  filtrarEnderecos(event: Event): void {
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.listaDeEnderecos = query
      ? this.listaDeEnderecosOriginal.filter((e) =>
        (e.labelEndereco || '').toLowerCase().includes(query)
      )
      : [...this.listaDeEnderecosOriginal];
  }

  async toggleEndereco(endereco: any, checked: boolean): Promise<void> {
    if (this.enderecoSelecionado) {
      this.enderecoSelecionado.selecionado = false;
    }

    endereco.selecionado = checked;
    this.enderecoSelecionado = checked ? endereco : null;
    this.limparPessoas();

    if (checked) {
      await this.buscarPessoas(this.empresaSelecionada.idEmpresa);
    }

    this.cdr.detectChanges();
  }

  // ── Pessoas ───────────────────────────────────────────────

  limparPessoas(): void {
    this.pessoas.data = [];
    this.listaCompleta = [];
    this.pessoasSelecionadasLocal = [];
    this.todosSelecionados = false;
    this.dataInicio = null;
    this.dataFim = null;
    this.total = 0;
  }

  async buscarPessoas(idEmpresa: number): Promise<void> {
    this.isLoadingPessoas = true;
    this.cdr.detectChanges();

    try {
      const resultado = await this.pessoasService.buscarPessoasPorEmpresaEGrupo(
        [idEmpresa],
        null
      );

      this.listaCompleta = resultado.map((p) => ({ ...p, selecionada: false }));
      this.pessoas.data = [...this.listaCompleta];
      this.total = this.listaCompleta.length;
      this.atualizarContagem(0, this.pageSize);
    } catch (error) {
      console.error('Erro ao buscar pessoas:', error);
      this.pessoas.data = [];
    } finally {
      this.isLoadingPessoas = false;
      this.cdr.detectChanges();
    }
  }

  pessoaSelecionada(pessoa: PessoaTable): boolean {
    return this.pessoasSelecionadasLocal.some((p) => p.idPessoa === pessoa.idPessoa);
  }

  alternarPessoaSelecionada(pessoa: PessoaTable, isChecked: boolean): void {
    pessoa.selecionada = isChecked;

    if (isChecked) {
      this.pessoasSelecionadasLocal.push(pessoa);
    } else {
      this.pessoasSelecionadasLocal = this.pessoasSelecionadasLocal.filter(
        (p) => p.idPessoa !== pessoa.idPessoa
      );
    }

    this.todosSelecionados =
      this.listaCompleta.length > 0 &&
      this.listaCompleta.every((p) => this.pessoaSelecionada(p));

    this.cdr.detectChanges();
  }

  toggleTodos(checked: boolean): void {
    this.todosSelecionados = checked;
    this.listaCompleta.forEach((p) => (p.selecionada = checked));
    this.pessoasSelecionadasLocal = checked ? [...this.listaCompleta] : [];
    this.cdr.detectChanges();
  }

  filtrarPessoas(event: Event): void {
    this.pessoas.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.atualizarContagem(event.pageIndex, event.pageSize);
  }

  atualizarContagem(pageIndex: number, pageSize: number): void {
    this.inicio = pageIndex * pageSize + 1;
    this.fim = Math.min((pageIndex + 1) * pageSize, this.total);
  }

  // ── Exportação ────────────────────────────────────────────

  async gerarArquivo(): Promise<void> {
    const body: ExportarDadosRequest = {
      idEmpresa: this.empresaSelecionada.idEmpresa,
      idEstabelecimento: this.enderecoSelecionado.idEstabelecimento,
      idsPessoas: this.pessoasSelecionadasLocal.map(p => p.idPessoa),
      dataInicio: _moment(this.dataInicio).format('YYYY-MM-DD'),
      dataFim: _moment(this.dataFim).format('YYYY-MM-DD'),
    };

    try {
      await this.exportarDadosService.exportarDados(body);
    } catch (err) {
      console.error('Erro ao exportar:', err);
    }
  }
}